<template>
  <div class="user-map" ref="userMap">
    <mapbox ref="map" @load="onLoad"></mapbox>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import Mapbox from 'views/elements/map/Mapbox';
import MapboxWrapper from 'views/elements/map/MapboxWrapper';
import { mapState } from 'vuex';
import { select } from 'd3-selection';
import icons from 'utils/icons';
import {
  map,
  filter,
  reduce,
  forEach,
  flatten,
  has,
  reverse,
  get,
} from 'lodash';

export default {
  components: {
    Mapbox,
  },

  mixins: [MapboxWrapper],

  data() {
    return {
      trajectoryLayers: [],
      locationMarkers: [],
      popupData: {},
      icons: icons,
    };
  },

  computed: {
    ...mapState('user', ['event_history', 'location']),

    eventsWithLocation() {
      return filter(
        this.event_history,
        item => item.longitude && item.latitude
      );
    },

    eventsWithTrajectory() {
      let events = filter(this.event_history, item => {
        return (
          has(item, 'trajectory.encoded') && item.trajectory.encoded !== null
        );
      });

      events = map(events, item => {
        let _coordinates = polyline.decode(item.trajectory.encoded);

        _coordinates = map(_coordinates, o => reverse(o));

        return { ...item, _coordinates };
      });

      return events;
    },

    allLocationCoordinates() {
      const coordinates = map(this.eventsWithLocation, item => [
        item.longitude,
        item.latitude,
      ]);

      return coordinates;
    },

    allTrajectoryCoordinates() {
      const coordinates = map(
        this.eventsWithTrajectory,
        item => item._coordinates
      );

      return flatten(coordinates);
    },
  },

  watch: {
    eventsWithTrajectory() {
      this.updateLocations();
      this.updateTrajectories();
    },
  },

  activated() {},

  methods: {
    onLoad() {
      this.updateLocations();
      this.updateTrajectories();
    },

    updateLocations() {
      this.removeLocationLayers();

      forEach(this.eventsWithLocation, (item, index) => {
        let html = '';

        if (icons[item.location.significance]) {
          html = `<i class="material-icons">${
            icons[item.location.significance]
          }</i>`;
        } else {
          html = '<i class="dot"></i>';
        }

        const el = select(this.$refs.userMap)
          .append('div')
          .attr(
            'class',
            icons[item.location.significance] ? 'marker' : 'marker is-small'
          )
          .html(html);

        const marker = new mapboxgl.Marker(el.node())
          .setLngLat([item.longitude, item.latitude])
          .addTo(this.map);

        this.locationMarkers = [...this.locationMarkers, marker];
      });

      if (this.eventsWithTrajectory.length === 0) {
        if (this.allLocationCoordinates.length > 1) {
          this.setBounds(this.allLocationCoordinates);
        } else {
          this.map.setCenter(this.allLocationCoordinates[0]).zoomTo(10);
        }
      }
    },

    updateTrajectories() {
      this.removeTrajectoryLayers();

      forEach(this.eventsWithTrajectory, (item, index) => {
        const id = `trajectory-${index}`;

        const layer = this.createLineLayer(id, item._coordinates);

        this.map.addLayer(layer);
        this.trajectoryLayers = [...this.trajectoryLayers, id];
      });

      this.setBounds(this.allTrajectoryCoordinates);
    },

    removeLocationLayers() {
      forEach(this.locationMarkers, marker => {
        marker.remove();
      });

      this.locationMarkers = [];
    },

    removeTrajectoryLayers() {
      forEach(this.trajectoryLayers, id => {
        this.map.removeLayer(id);
        this.map.removeSource(id);
      });

      this.trajectoryLayers = [];
    },

    createLineLayer(id, coordinates) {
      return {
        id: id,
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: coordinates,
            },
          },
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#5886e1',
          'line-width': 2,
        },
      };
    },

    setBounds(coordinates) {
      if (coordinates.length === 0) {
        const longitude = get(this.location, 'longitude');
        const latitude = get(this.location, 'latitude');

        if (longitude && latitude) {
          this.map.setCenter([longitude, latitude]).setZoom(5);
        }

        return;
      }

      const bounds = reduce(
        coordinates,
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
      );

      this.map.fitBounds(bounds, { padding: 40 });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.user-map {
  position: relative;
  width: 100%;
  height: 100%;
  box-shadow: 0 2px 4px 0 rgba(58, 153, 252, 0.08),
    0 1px 2px 0 rgba($black, 0.08);
}
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.user-map {
  .marker {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    background-color: $white;
    border: 1px solid rgba(74, 74, 74, 0.24);
    box-shadow: 0 0 16px 2px rgba(88, 134, 225, 0.24);
    border-radius: 50%;

    &.is-small {
      width: 14px;
      height: 14px;
      box-shadow: 0 0 6px 0 rgba($black, 0.24);
      border: solid 1px rgb(88, 134, 225);
    }

    i {
      &.material-icons {
        font-size: 14px;
        line-height: 1.4;
        color: rgba($black, 0.87);
      }

      &.dot {
        display: flex;
        align-self: center;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: $blue;
      }
    }
  }
}
</style>

